import React from 'react';

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Box, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material';

import type { DestinationContentTag } from '~/services/TagsService';

type Props = {
  tag: DestinationContentTag;
  index: number;
  total: number;
  onMove: (tagId: string, direction: 'up' | 'down') => void;
};

export default function SortableDestinationContentTag({ tag, index, total, onMove }: Props) {
  const { attributes, isDragging, listeners, setNodeRef, transform, transition } = useSortable({ id: tag.id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <Paper ref={setNodeRef} style={style} variant="outlined">
      <Stack direction="row" alignItems="center" spacing={1} sx={{ p: 1 }}>
        <IconButton
          {...attributes}
          {...listeners}
          aria-label={`Drag ${tag.name} to reorder`}
          size="small"
          sx={{ cursor: 'grab', '&:active': { cursor: 'grabbing' } }}
        >
          <DragIndicatorIcon fontSize="small" />
        </IconButton>
        <Typography color="text.secondary" sx={{ minWidth: 24 }} variant="body2">
          {index + 1}.
        </Typography>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography noWrap variant="body2">
            {tag.name}
          </Typography>
          {tag.displayOrder === null && (
            <Typography color="text.secondary" variant="caption">
              Unranked
            </Typography>
          )}
        </Box>
        <Tooltip title="Move up">
          <span>
            <IconButton
              aria-label={`Move ${tag.name} up`}
              disabled={index === 0}
              onClick={() => onMove(tag.id, 'up')}
              size="small"
            >
              <KeyboardArrowUpIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Move down">
          <span>
            <IconButton
              aria-label={`Move ${tag.name} down`}
              disabled={index === total - 1}
              onClick={() => onMove(tag.id, 'down')}
              size="small"
            >
              <KeyboardArrowDownIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </Paper>
  );
}
